import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API_URL from "../Config";
import { useAuth } from "../context/AuthContext";
import AssetBackdrop from "./AssetBackdrop";
import MigArchCredit from "./MigArchCredit";
import PasswordInput from "./PasswordInput";
import "./Login.css";

/**
 * Sign-in screen.
 *
 * A 401 here means the credentials were wrong - there is no token to refresh
 * at that point, so it is reported as such.
 */

function Login() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!phoneNumber) return toast.error("Phone number is required!");
    if (!password) return toast.error("Password is required!");

    setSubmitting(true);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/login`,
        { phone_number: phoneNumber, password },
        { headers: { "Content-Type": "application/json" } }
      );

      // AuthProvider stores the tokens and then loads the real profile, so the
      // sidebar and role are known before the first protected page renders.
      await login(data.access_token, data.refresh_token);
      toast.success("Login successful!");
      navigate("/Dashboard", { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      if (err.response?.status === 401) {
        toast.error("Invalid phone number or password.");
      } else if (err.response?.status === 403) {
        toast.info(
          err.response.data?.message ||
            "Your account is awaiting admin approval."
        );
      } else {
        toast.error("Could not sign in. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <ToastContainer />
      <AssetBackdrop />

      <div className="login-box">
        <div className="text-center">
          <div className="icon-wrapper">
            <img src="/logo-jumig-store.png" alt="JUMIG Store" />
          </div>
          <p className="login-kicker">Archive Portal</p>
          <h1 className="title">Portal Access</h1>
          <p className="subtitle">
            Sign in to review the JUMIG Store asset library.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            {/* type="tel" rather than type="number": a phone number is a digit
                string, not a quantity, and number inputs add spinners and
                silently accept values like "1e5". */}
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Phone Number"
              value={phoneNumber}
              onChange={(event) =>
                setPhoneNumber(event.target.value.replace(/\D/g, ""))
              }
              required
              autoComplete="tel"
              className="no-spinner"
            />
          </div>

          <div className="input-group">
            <PasswordInput
              placeholder="Password"
              aria-label="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <div className="login-forgot">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>

          <button type="submit" className="login-button" disabled={submitting}>
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="login-register">
          Don't have an account yet?{" "}
          <Link to="/registration">Sign up here</Link>
        </div>

        <MigArchCredit className="login-credit" />
      </div>
    </div>
  );
}

export default Login;
